import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';

import { Project } from '../shared/interfaces/project.interface';
import { ProjectGroup } from '../shared/interfaces/project-group.interface';
import { ProjectRouteStation } from '../shared/interfaces/project-route-station.interface';
import { ProjectImageData } from '../shared/interfaces/project-image-data.interface';
import { ProjectDataService } from '../shared/services/project-data.service';
import { ProjectService } from '../shared/services/project.service';
import { RouteStationService } from '../shared/services/route-station.service';
import { TimerSwitchService } from '../shared/services/timer-switch.service';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import { ModalService } from 'src/app/shared/modal/shared/modal.service';
import { MetaInformationService } from '../shared/services/meta-information.service';
import { ProjectGroupMetaInformation } from '../shared/interfaces/project-group-meta-information';
import { ProjectTimeSwitchBody } from '../project-time-switch/shared/interfaces/project-time-switch-body';
import { ProjectTimeSwitchInterface } from '../project-time-switch/shared/interfaces/project-time-switch.interface';
import { AddTimeSwitchComponent } from '../project-time-switch/project-time-switch-add-modal/add-time-switch/add-time-switch.component';
import { getVerkehrsstufeSelect } from '../shared/enums/verkehrsstufe';
import { getVerkehrszustandSelect } from '../shared/enums/verkehrszustand';
import { laneColorHex } from './shared/time-switch-component-types';

/** Ein aufgelöstes Segment auf der Ergebnis-Zeitleiste eines Tages. */
interface ResultSegment {
  startMin: number;
  endMin: number;
  sp: ProjectTimeSwitchInterface;
  oneTime: boolean;
}

/** Eine Ausgangs-Zeile der Ergebnis-Zeitleiste. */
interface ResultLane {
  key: 'aq' | 'mq' | 'blinker' | 'do' | 'di';
  label: string;
  sub: string;
  segments: ResultSegment[];
}

const WEEKDAY_KEYS: (keyof ProjectTimeSwitchInterface)[] =
  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/**
 * Neue Zeitschaltuhr-Ansicht (Konzept). Läuft parallel zur bestehenden Ansicht.
 * Zeigt das zusammengeführte Ergebnis je Ausgang und weist Zeitschaltuhren nur
 * Stationen zu, die die verwendeten AQ-Stellcodes auch kennen.
 */
@Component({
  selector: 'avs-project-time-switch-neu',
  templateUrl: './project-time-switch-neu.component.html',
  styleUrls: ['./project-time-switch-neu.component.css']
})
export class ProjectTimeSwitchNeuComponent implements OnInit, OnDestroy {
  public activeProject?: Project;
  public activeGroup?: ProjectGroup;
  public projectStations: ProjectRouteStation[] = [];
  public allImages: ProjectImageData[] = [];

  public timerSwitches: ProjectTimeSwitchBody[] = [];
  public selectedTimer?: ProjectTimeSwitchBody;

  public readonly weekdayLabels = WEEKDAY_LABELS;
  public lanes: ResultLane[] = [];
  public windowStart: Date = new Date();
  public windowDays = 7;
  public dayTicks: { label: string, leftPct: number }[] = [];

  // Kombinierte Hover-Vorschau (Bild + Text)
  public hoverImage?: string;
  public hoverText?: string;
  public hoverOff = false;
  public hoverX = 0;
  public hoverY = 0;

  /** groupId -> Metainformationen der Gruppe (für die Meta-Anzeige je Station im Zuweisungs-Panel). */
  public metaByGroup = new Map<number, ProjectGroupMetaInformation[]>();
  /** Gruppen des Projekts (für die Gruppen-Schnellauswahl im Dialog). */
  public projectGroupsList: ProjectGroup[] = [];

  public readonly apiAddress = environment.apiAddress;
  public readonly verkehrsstufe = getVerkehrsstufeSelect();
  public readonly verkehrszustand = getVerkehrszustandSelect();
  public readonly functionOptions = [{ name: 'Aus', id: 0 }, { name: 'An', id: 1 }];
  public readonly weekdayLabelsFull = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  // Editor-State (neuer Schaltpunkt)
  public editorOpen = false;
  public sbPickerOpen = false;
  public stellbildOptions: { stellcode: number, path?: string }[] = [];
  public draft = this.blankDraft();

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly projectDataService: ProjectDataService,
    private readonly projectService: ProjectService,
    private readonly routeStationService: RouteStationService,
    private readonly timerService: TimerSwitchService,
    private readonly alertService: AlertService,
    private readonly modalService: ModalService,
    private readonly metaInformationService: MetaInformationService,
    private readonly dialog: MatDialog
  ) { }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.projectDataService.activeProject.subscribe(project => {
        this.activeProject = project;
        this.loadImages();
        this.loadProjectStations();
        this.loadTimerSwitches();
      }),
      this.projectDataService.activeGroup.subscribe(group => { this.activeGroup = group; })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // ---------- Laden / Auswahl ----------

  /** Projektweite Stellbilder laden (für Stellcode-Anzeige und Zuweisungsfilter). */
  private loadImages(): void {
    if (!this.activeProject) { this.allImages = []; return; }
    this.projectService.getAllRouteStationImages(this.activeProject.id).subscribe(images => {
      this.allImages = images ?? [];
      this.buildLanes();
    });
  }

  /** Alle Stationen des Projekts laden (Zeitschaltuhr ist projekt-, nicht gruppengebunden). */
  private loadProjectStations(): void {
    const project = this.activeProject;
    if (!project) { this.projectStations = []; return; }
    const applyGroups = (groups: ProjectGroup[]) => {
      this.projectGroupsList = groups;
      const groupIds = new Set(groups.map(g => g.id));
      this.routeStationService.getRouteStations().subscribe(all => {
        this.projectStations = (all ?? []).filter(s => groupIds.has(s.groupId));
        this.loadMetaByGroup();
      });
    };
    if (project.groups && project.groups.length > 0) { applyGroups(project.groups); }
    else { this.projectService.getProjectGroups(project.id).subscribe(groups => applyGroups(groups ?? [])); }
  }

  private loadTimerSwitches(): void {
    if (!this.activeProject) {
      this.timerSwitches = [];
      this.selectedTimer = undefined;
      this.lanes = [];
      return;
    }
    this.timerService.getTimerSwitchByProjectId(this.activeProject.id).subscribe(timers => {
      this.timerSwitches = timers ?? [];
      const keepId = this.selectedTimer?.id;
      this.selectedTimer = this.timerSwitches.find(t => t.id === keepId) ?? this.timerSwitches[0];
      this.onTimerChanged();
    });
  }

  public selectTimer(timer: ProjectTimeSwitchBody): void {
    this.selectedTimer = timer;
    this.onTimerChanged();
  }

  private onTimerChanged(): void {
    this.initWindow();
    this.buildLanes();
  }

  public shiftWindow(direction: 'prev' | 'next'): void {
    const delta = direction === 'next' ? this.windowDays : -this.windowDays;
    this.windowStart = this.addDays(this.windowStart, delta);
    this.buildLanes();
  }

  public setWindowDays(days: number): void {
    this.windowDays = days;
    this.buildLanes();
  }

  public get windowLabel(): string {
    const end = this.addDays(this.windowStart, this.windowDays - 1);
    return this.dateShort(this.windowStart) + ' – ' + this.dateShort(end);
  }

  private initWindow(): void {
    let start = this.startOfDay(new Date());
    const ts = this.selectedTimer;
    if (ts?.start) {
      const s = this.startOfDay(new Date(ts.start));
      if (s.getTime() > start.getTime()) { start = s; }
    }
    const mon0 = (start.getDay() + 6) % 7;
    this.windowStart = this.addDays(start, -mon0);
  }

  // ---------- Ergebnis-Zeitleiste (kombiniert: wiederkehrend + einmalig) ----------

  private buildLanes(): void {
    const ts = this.selectedTimer;
    if (!ts) { this.lanes = []; this.dayTicks = []; return; }
    this.dayTicks = [];
    for (let d = 0; d < this.windowDays; d++) {
      const date = this.addDays(this.windowStart, d);
      this.dayTicks.push({
        label: this.weekdayLabels[(date.getDay() + 6) % 7] + ' ' + date.getDate() + '.' + (date.getMonth() + 1) + '.',
        leftPct: (d + 0.5) / this.windowDays * 100
      });
    }
    const built: ResultLane[] = [
      { key: 'aq', label: 'AQ', sub: 'Anzeigequerschnitt', segments: this.resolveWindow(ts.aqTimers) },
      { key: 'mq', label: 'MQ', sub: 'Messquerschnitt', segments: this.resolveWindow(ts.mqTimers) },
      { key: 'blinker', label: 'Blinker', sub: '', segments: this.resolveWindow(ts.blinkerTimers) },
      { key: 'do', label: 'DO', sub: 'Digitalausgang', segments: this.resolveWindow(ts.doTimers) },
      { key: 'di', label: 'DI', sub: 'Digitaleingang', segments: this.resolveWindow(ts.diTimers) }
    ];
    this.lanes = built.filter(lane => lane.segments.length > 0);
  }

  /**
   * Führt wiederkehrende und einmalige Schaltpunkte über das Datumsfenster zusammen
   * und löst Überschneidungen anhand der Priorität auf (kleinster Wert gewinnt).
   * Einmalig und wiederkehrend konkurrieren gleichberechtigt – wer gewinnt, entscheidet die Priorität.
   */
  private resolveWindow(list: ProjectTimeSwitchInterface[] | undefined): ResultSegment[] {
    const points = list ?? [];
    if (points.length === 0) { return []; }
    const total = this.windowDays * 1440;
    const winner: (ProjectTimeSwitchInterface | null)[] = new Array(total).fill(null);

    // Gesamtgültigkeit der Zeitschaltuhr (Start/Stop) als Grenzen vorbereiten
    const ts = this.selectedTimer;
    let vFromVal = -Infinity, vToVal = Infinity, vFromMin = 0, vToMin = 1440;
    if (ts?.start) { const s = new Date(ts.start); vFromVal = this.dateVal(s.getFullYear(), s.getMonth() + 1, s.getDate()); vFromMin = s.getHours() * 60 + s.getMinutes(); }
    if (ts?.stop) { const e = new Date(ts.stop); vToVal = this.dateVal(e.getFullYear(), e.getMonth() + 1, e.getDate()); vToMin = e.getHours() * 60 + e.getMinutes(); }

    for (let d = 0; d < this.windowDays; d++) {
      const date = this.addDays(this.windowStart, d);
      const dayKey = WEEKDAY_KEYS[(date.getDay() + 6) % 7];
      const cur = this.dateVal(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const base = d * 1440;

      // Außerhalb der Gültigkeit keine Ergebnisse; an Randtagen auf die gültige Zeit begrenzen
      if (cur < vFromVal || cur > vToVal) { continue; }
      const validOn = cur === vFromVal ? vFromMin : 0;
      const validOff = cur === vToVal ? vToMin : 1440;
      if (validOff <= validOn) { continue; }

      for (const sp of points) {
        let onMin: number, offMin: number, wrap = false;
        if (!sp.one_time) {
          if (sp[dayKey] !== true) { continue; }
          onMin = this.toMinutes(sp.hourOn, sp.minuteOn);
          offMin = this.toMinutes(sp.hourOff, sp.minuteOff);
          if (offMin <= onMin) { wrap = true; }
        } else {
          const start = this.dateVal(sp.yearOn, sp.monthOn, sp.dayOn);
          const end = this.dateVal(sp.yearOff, sp.monthOff, sp.dayOff);
          if (cur < start || cur > end) { continue; }
          onMin = cur === start ? this.toMinutes(sp.hourOn, sp.minuteOn) : 0;
          offMin = cur === end ? this.toMinutes(sp.hourOff, sp.minuteOff) : 1440;
        }
        const cover = (from: number, to: number) => {
          const f = Math.max(from, validOn);
          const t = Math.min(to, validOff);
          for (let m = f; m < t; m++) {
            const idx = base + m;
            const c = winner[idx];
            if (c === null || sp.priority < c.priority) { winner[idx] = sp; }
          }
        };
        if (wrap) { cover(onMin, 1440); cover(0, offMin); } else { cover(onMin, offMin); }
      }
    }

    const segments: ResultSegment[] = [];
    let start = 0;
    for (let m = 1; m <= total; m++) {
      const prev = winner[m - 1];
      const cur = m < total ? winner[m] : undefined;
      if (m === total || cur?.id !== prev?.id) {
        if (prev) { segments.push({ startMin: start, endMin: m, sp: prev, oneTime: !!prev.one_time }); }
        start = m;
      }
    }
    return segments;
  }

  private toMinutes(hour?: number, minute?: number): number {
    return (hour ?? 0) * 60 + (minute ?? 0);
  }

  private addDays(base: Date, days: number): Date {
    return new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** Vertikale Tagesraster-Linien passend zur Fensterbreite. */
  public get trackBackground(): string {
    const pct = 100 / this.windowDays;
    return `repeating-linear-gradient(90deg, transparent, transparent calc(${pct}% - 1px), var(--tsn-line) calc(${pct}% - 1px), var(--tsn-line) ${pct}%)`;
  }

  private dateVal(year?: number, month?: number, day?: number): number {
    return (year ?? 0) * 10000 + (month ?? 0) * 100 + (day ?? 0);
  }

  private dateShort(d: Date): string {
    return this.weekdayLabels[(d.getDay() + 6) % 7] + ' ' + d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear();
  }

  public segmentLeft(seg: ResultSegment): string {
    return (seg.startMin / (this.windowDays * 1440) * 100) + '%';
  }

  public segmentWidth(seg: ResultSegment): string {
    return ((seg.endMin - seg.startMin) / (this.windowDays * 1440) * 100) + '%';
  }

  public segmentTime(seg: ResultSegment): string {
    return this.windowStamp(seg.startMin) + ' – ' + this.windowStamp(seg.endMin);
  }

  private windowStamp(min: number): string {
    const d = Math.floor(Math.min(min, this.windowDays * 1440 - 1) / 1440);
    const inDay = min - d * 1440;
    const date = this.addDays(this.windowStart, d);
    return this.weekdayLabels[(date.getDay() + 6) % 7] + ' ' + date.getDate() + '.' + (date.getMonth() + 1) + '. ' + this.formatMinutes(inDay);
  }

  private formatMinutes(min: number): string {
    const h = Math.floor(min / 60) % 24;
    const m = min % 60;
    return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
  }

  public onBlockEnter(event: MouseEvent, lane: ResultLane, seg: ResultSegment): void {
    this.hoverText = this.segmentLabel(lane, seg.sp) + '  ·  ' + this.segmentTime(seg);
    this.hoverImage = lane.key === 'aq' ? this.stellbildPath(seg.sp) : undefined;
    this.hoverOff = lane.key === 'aq' && seg.sp.tlS_Function === 0;
    this.movePreview(event);
  }

  public movePreview(event: MouseEvent): void {
    if (!this.hoverText) { return; }
    this.hoverX = event.clientX + 16;
    this.hoverY = event.clientY + 16;
  }

  public hidePreview(): void {
    this.hoverImage = undefined;
    this.hoverText = undefined;
    this.hoverOff = false;
  }

  public stellbildPath(sp: ProjectTimeSwitchInterface): string | undefined {
    if (sp.tlS_Code === undefined || sp.tlS_Code === null) { return undefined; }
    const image = this.allImages.find(img => img.stellcode === sp.tlS_Code);
    return image?.path ? this.apiAddress + image.path : undefined;
  }

  public segmentLabel(lane: ResultLane, sp: ProjectTimeSwitchInterface): string {
    switch (lane.key) {
      case 'aq': return (sp.tlS_Code !== undefined ? 'Stellcode ' + sp.tlS_Code : '') + (sp.tlS_Function === 0 ? ' · aus' : '');
      case 'mq': return this.nameById(this.verkehrsstufe, sp.trafic) + ' · ' + this.nameById(this.verkehrszustand, sp.vs);
      case 'blinker': return sp.tlS_Function === 0 ? 'Blinker aus' : 'Blinker an';
      default: return sp.state ? 'An' : 'Aus';
    }
  }

  private nameById(list: any[], id?: number): string {
    if (id === undefined || id === null) { return '–'; }
    return list.find(item => item.id === id)?.name ?? String(id);
  }

  /** Typfarbe (= Rand des Blocks) aus der zentralen Typdefinition. */
  public laneColor(key: 'aq' | 'mq' | 'blinker' | 'do' | 'di'): string {
    return laneColorHex(key);
  }

  /** Füllfarbe (= Zustand des Blocks). */
  public segmentFill(lane: ResultLane, sp: ProjectTimeSwitchInterface): string {
    switch (lane.key) {
      case 'aq': return '#ffffff';                                        // weiß, damit das Stellbild frei liegt
      case 'mq': return this.trafficColor(sp.trafic);                     // Verkehrsstufe: grün → rot
      case 'blinker': return sp.tlS_Function === 0 ? '#b0b4bc' : laneColorHex('blinker'); // aus = grau, sonst amber
      default: return sp.state ? '#1f9d55' : '#b0b4bc';                   // DO/DI: an = grün, aus = grau
    }
  }

  private trafficColor(stufe?: number): string {
    switch (stufe) {
      case 1: return '#1f9d55'; // freier Verkehr
      case 2: return '#f5c400'; // dichter Verkehr
      case 3: return '#f5820b'; // zähfließender Verkehr
      case 4: return '#e0342b'; // Stau
      default: return '#b0b4bc';
    }
  }


  private loadMetaByGroup(): void {
    const groupIds = Array.from(new Set(this.projectStations.map(s => s.groupId)));
    if (groupIds.length === 0) { this.metaByGroup = new Map<number, ProjectGroupMetaInformation[]>(); return; }
    const calls = groupIds.map(id => this.metaInformationService.getMetaInformationByGroupId(id));
    forkJoin(calls).subscribe(results => {
      const map = new Map<number, ProjectGroupMetaInformation[]>();
      results.forEach((meta, i) => map.set(groupIds[i], meta ?? []));
      this.metaByGroup = map;
    });
  }


  // ---------- Zeitschaltuhr anlegen / aktivieren / löschen ----------

  public onAddTimer(): void {
    const dialogRef = this.dialog.open(AddTimeSwitchComponent);
    dialogRef.afterClosed().subscribe(result => {
      if (!result || !this.activeProject) { return; }
      const startParts = result.starttime.split(':', 3);
      const endParts = result.endtime.split(':', 3);
      const startEvent = new Date(result.startDate.year, result.startDate.month - 1, result.startDate.day, startParts[0], startParts[1], startParts[2], 0);
      const endEvent = new Date(result.endDate.year, result.endDate.month - 1, result.endDate.day, endParts[0], endParts[1], endParts[2], 0);
      const data: ProjectTimeSwitchBody = {
        projectId: this.activeProject.id,
        start: startEvent.toISOString() as unknown as Date,
        stop: endEvent.toISOString() as unknown as Date,
        name: result.name
      };
      this.timerService.createTimerSwitch(data).subscribe(() => this.loadTimerSwitches());
    });
  }

  public onEditTimer(ts: ProjectTimeSwitchBody): void {
    const dialogRef = this.dialog.open(AddTimeSwitchComponent, { data: ts });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result || !this.activeProject) { return; }
      const startParts = result.starttime.split(':', 3);
      const endParts = result.endtime.split(':', 3);
      const startEvent = new Date(result.startDate.year, result.startDate.month - 1, result.startDate.day, startParts[0], startParts[1], startParts[2], 0);
      const endEvent = new Date(result.endDate.year, result.endDate.month - 1, result.endDate.day, endParts[0], endParts[1], endParts[2], 0);
      const data: ProjectTimeSwitchBody = {
        id: result.id ?? ts.id,
        projectId: this.activeProject.id,
        start: startEvent.toISOString() as unknown as Date,
        stop: endEvent.toISOString() as unknown as Date,
        name: result.name
      };
      this.timerService.updateTimerSwitch(data).subscribe(() => this.loadTimerSwitches());
    });
  }

  public toggleEnabled(ts: ProjectTimeSwitchBody): void {
    ts.enabled = !ts.enabled;
    this.timerService.updateTimerSwitch(ts).subscribe(() => {
      this.alertService.alert(
        ts.enabled ? 'Die Zeitschaltuhr wurde aktiviert' : 'Die Zeitschaltuhr wurde deaktiviert',
        AlertTypes.info
      );
    });
  }

  public onDeleteTimer(ts: ProjectTimeSwitchBody): void {
    if (!ts.id) { return; }
    this.modalService.openDialog('Zeitschaltuhr löschen?', 'Nein', 'Ja').subscribe(() => {
      this.timerService.deleteTimerSwitch(ts.id!).subscribe(() => {
        this.alertService.alert('Die Zeitschaltuhr wurde erfolgreich gelöscht', AlertTypes.info);
        if (this.selectedTimer?.id === ts.id) { this.selectedTimer = undefined; }
        this.loadTimerSwitches();
      });
    });
  }

  // ---------- Editor: neuer Schaltpunkt ----------

  private blankDraft() {
    return {
      id: undefined as number | undefined,
      priority: 2,
      component: 'aq' as 'aq' | 'mq' | 'blinker' | 'do' | 'di',
      oneTime: false,
      weekdays: [true, true, true, true, true, false, false],
      onTime: '07:00',
      offTime: '09:00',
      startDate: '',
      startTime: '18:00',
      endDate: '',
      endTime: '06:00',
      tlS_Code: undefined as number | undefined,
      tlS_Function: 1,
      tlS_Time: 0,
      coupleBlinker: false,
      trafic: undefined as number | undefined,
      vs: undefined as number | undefined,
      state: true
    };
  }

  public openEditor(): void {
    if (!this.selectedTimer) { return; }
    this.draft = this.blankDraft();
    this.buildStellbildOptions();
    this.editorOpen = true;
  }

  public closeEditor(): void {
    this.editorOpen = false;
    this.sbPickerOpen = false;
  }

  public setDraftComponent(component: 'aq' | 'mq' | 'blinker' | 'do' | 'di'): void {
    this.draft.component = component;
  }

  public toggleWeekday(index: number): void {
    this.draft.weekdays[index] = !this.draft.weekdays[index];
  }

  private buildStellbildOptions(): void {
    const seen = new Set<number>();
    const options: { stellcode: number, path?: string }[] = [];
    for (const img of this.allImages) {
      if (img.stellcode === undefined || img.stellcode === null || seen.has(img.stellcode)) { continue; }
      seen.add(img.stellcode);
      options.push({ stellcode: img.stellcode, path: img.path ? this.apiAddress + img.path : undefined });
    }
    options.sort((a, b) => a.stellcode - b.stellcode);
    this.stellbildOptions = options;
  }

  public selectStellbild(option: { stellcode: number, path?: string }): void {
    this.draft.tlS_Code = option.stellcode;
    this.sbPickerOpen = false;
  }

  public draftStellbildPath(): string | undefined {
    return this.stellbildOptions.find(o => o.stellcode === this.draft.tlS_Code)?.path;
  }

  private parseTime(value: string): [number, number] {
    const parts = (value || '0:0').split(':');
    return [Number(parts[0]) || 0, Number(parts[1]) || 0];
  }

  private buildCommonPayload(): any {
    const payload: any = {
      projectId: this.activeProject!.id,
      secondOn: 0, secondOff: 0,
      priority: this.draft.priority,
      timerSwitchId: this.selectedTimer!.id,
      one_time: this.draft.oneTime,
      monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false,
      yearOn: 0, monthOn: 0, dayOn: 0, yearOff: 0, monthOff: 0, dayOff: 0
    };
    if (this.draft.oneTime) {
      const [onH, onM] = this.parseTime(this.draft.startTime);
      const [offH, offM] = this.parseTime(this.draft.endTime);
      payload.hourOn = onH; payload.minuteOn = onM; payload.hourOff = offH; payload.minuteOff = offM;
      const start = this.draft.startDate.split('-');
      const end = this.draft.endDate.split('-');
      payload.yearOn = Number(start[0]) || 0; payload.monthOn = Number(start[1]) || 0; payload.dayOn = Number(start[2]) || 0;
      payload.yearOff = Number(end[0]) || 0; payload.monthOff = Number(end[1]) || 0; payload.dayOff = Number(end[2]) || 0;
    } else {
      const [onH, onM] = this.parseTime(this.draft.onTime);
      const [offH, offM] = this.parseTime(this.draft.offTime);
      payload.hourOn = onH; payload.minuteOn = onM; payload.hourOff = offH; payload.minuteOff = offM;
      const keys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      this.draft.weekdays.forEach((value, i) => { payload[keys[i]] = value; });
    }
    return payload;
  }

  public saveSwitchPoint(): void {
    if (!this.selectedTimer?.id || !this.activeProject) { return; }
    const id = this.selectedTimer.id;

    if (!this.draft.oneTime && !this.draft.weekdays.some(Boolean)) {
      this.alertService.alert('Bitte mindestens einen Wochentag wählen', AlertTypes.warning); return;
    }
    if (this.draft.oneTime && (!this.draft.startDate || !this.draft.endDate)) {
      this.alertService.alert('Bitte Start- und Enddatum wählen', AlertTypes.warning); return;
    }
    if (this.draft.component === 'aq' && (this.draft.tlS_Code === undefined || this.draft.tlS_Code === null)) {
      this.alertService.alert('Bitte ein Stellbild wählen', AlertTypes.warning); return;
    }
    if (this.draft.component === 'mq' && (this.draft.trafic === undefined || this.draft.vs === undefined)) {
      this.alertService.alert('Bitte Verkehrsstufe und Verkehrszustand wählen', AlertTypes.warning); return;
    }

    const key = this.draft.component;
    const editing = this.draft.id !== undefined;
    // Neue Schaltpunkte erhalten eine eindeutige Priorität am Ende des Stapels
    // (verhindert gleiche Priorität zur gleichen Zeit). Beim Bearbeiten bleibt sie erhalten.
    if (!editing) { this.draft.priority = this.timerArray(key).length; }

    const base = this.buildCommonPayload();
    if (editing) { base.id = this.draft.id; }

    let payload: any;
    switch (key) {
      case 'aq': payload = { ...base, tlS_Code: this.draft.tlS_Code, tlS_Function: this.draft.tlS_Function, tlS_Time: this.draft.tlS_Function === 2 ? this.draft.tlS_Time : 0 }; break;
      case 'mq': payload = { ...base, trafic: this.draft.trafic, vs: this.draft.vs }; break;
      case 'blinker': payload = { ...base, tlS_Code: 61, tlS_Function: this.draft.tlS_Function, tlS_Time: 0 }; break;
      default: payload = { ...base, state: this.draft.state }; break;
    }

    const done = () => {
      this.alertService.alert('Schaltpunkt gespeichert', AlertTypes.info);
      this.loadTimerSwitches();
      this.closeEditor();
    };

    if (editing) {
      this.updateObservable(key, id, payload).subscribe(() => done());
      return;
    }

    // Anlegen
    if (key === 'aq' && this.draft.tlS_Code !== 61) {
      this.timerService.createAQTimer(id, payload).subscribe(() => {
        if (this.draft.coupleBlinker) {
          const blinker = { ...base, priority: this.timerArray('blinker').length, tlS_Code: 61, tlS_Function: 1, tlS_Time: 0 };
          this.timerService.createBlinkerTimer(id, blinker).subscribe(() => done());
        } else { done(); }
      });
      return;
    }
    switch (key) {
      case 'aq': this.timerService.createBlinkerTimer(id, payload).subscribe(() => done()); break; // tlS_Code === 61
      case 'mq': this.timerService.createMQTimer(id, payload).subscribe(() => done()); break;
      case 'blinker': this.timerService.createBlinkerTimer(id, payload).subscribe(() => done()); break;
      case 'do': this.timerService.createDoTimer(id, payload).subscribe(() => done()); break;
      case 'di': this.timerService.createDiTimer(id, payload).subscribe(() => done()); break;
    }
  }

  // ---------- Schaltpunkte verwalten: bearbeiten / löschen / Vorrang ----------

  private timerArray(key: 'aq' | 'mq' | 'blinker' | 'do' | 'di'): ProjectTimeSwitchInterface[] {
    const ts = this.selectedTimer;
    if (!ts) { return []; }
    switch (key) {
      case 'aq': return ts.aqTimers ?? [];
      case 'mq': return ts.mqTimers ?? [];
      case 'blinker': return ts.blinkerTimers ?? [];
      case 'do': return ts.doTimers ?? [];
      case 'di': return ts.diTimers ?? [];
    }
  }

  /** Alle Komponenten, die Schaltpunkte haben – für den Verwalten-Bereich. */
  public get managedComponents(): { key: 'aq' | 'mq' | 'blinker' | 'do' | 'di', label: string }[] {
    const all: { key: 'aq' | 'mq' | 'blinker' | 'do' | 'di', label: string }[] = [
      { key: 'aq', label: 'AQ' }, { key: 'mq', label: 'MQ' }, { key: 'blinker', label: 'Blinker' },
      { key: 'do', label: 'DO' }, { key: 'di', label: 'DI' }
    ];
    return all.filter(c => this.timerArray(c.key).length > 0);
  }

  /** Schaltpunkte einer Komponente in Vorrang-Reihenfolge (kleinste Priorität zuerst = gewinnt). */
  public managedPoints(key: 'aq' | 'mq' | 'blinker' | 'do' | 'di'): ProjectTimeSwitchInterface[] {
    return [...this.timerArray(key)].sort((a, b) => (a.priority - b.priority) || ((a.id ?? 0) - (b.id ?? 0)));
  }

  public pointSummary(key: 'aq' | 'mq' | 'blinker' | 'do' | 'di', sp: ProjectTimeSwitchInterface): string {
    const when = sp.one_time
      ? `${sp.dayOn}.${sp.monthOn}.${sp.yearOn} – ${sp.dayOff}.${sp.monthOff}.${sp.yearOff}`
      : `${this.weekdaySummary(sp)}  ${this.fmt(sp.hourOn, sp.minuteOn)}–${this.fmt(sp.hourOff, sp.minuteOff)}`;
    let what = '';
    if (key === 'aq') { what = sp.tlS_Code !== undefined ? `Stellcode ${sp.tlS_Code}` : ''; }
    else if (key === 'mq') { what = `${this.nameById(this.verkehrsstufe, sp.trafic)} · ${this.nameById(this.verkehrszustand, sp.vs)}`; }
    else if (key === 'blinker') { what = sp.tlS_Function === 0 ? 'Blinker aus' : 'Blinker an'; }
    else { what = sp.state ? 'An' : 'Aus'; }
    return `${what} · ${when}`;
  }

  private weekdaySummary(sp: ProjectTimeSwitchInterface): string {
    const flags = [sp.monday, sp.tuesday, sp.wednesday, sp.thursday, sp.friday, sp.saturday, sp.sunday];
    return this.weekdayLabels.filter((_, i) => flags[i]).join(' ') || '–';
  }

  private fmt(hour?: number, minute?: number): string {
    return String(hour ?? 0).padStart(2, '0') + ':' + String(minute ?? 0).padStart(2, '0');
  }

  private fmtDate(year?: number, month?: number, day?: number): string {
    if (!year) { return ''; }
    return `${year}-${String(month ?? 1).padStart(2, '0')}-${String(day ?? 1).padStart(2, '0')}`;
  }

  public editPoint(key: 'aq' | 'mq' | 'blinker' | 'do' | 'di', sp: ProjectTimeSwitchInterface): void {
    this.draft = this.blankDraft();
    this.buildStellbildOptions();
    this.draft.id = sp.id;
    this.draft.priority = sp.priority;
    this.draft.component = key;
    this.draft.oneTime = !!sp.one_time;
    this.draft.weekdays = [sp.monday, sp.tuesday, sp.wednesday, sp.thursday, sp.friday, sp.saturday, sp.sunday].map(v => !!v);
    this.draft.onTime = this.fmt(sp.hourOn, sp.minuteOn);
    this.draft.offTime = this.fmt(sp.hourOff, sp.minuteOff);
    this.draft.startTime = this.fmt(sp.hourOn, sp.minuteOn);
    this.draft.endTime = this.fmt(sp.hourOff, sp.minuteOff);
    this.draft.startDate = this.fmtDate(sp.yearOn, sp.monthOn, sp.dayOn);
    this.draft.endDate = this.fmtDate(sp.yearOff, sp.monthOff, sp.dayOff);
    this.draft.tlS_Code = sp.tlS_Code;
    this.draft.tlS_Function = sp.tlS_Function ?? 1;
    this.draft.tlS_Time = sp.tlS_Time ?? 0;
    this.draft.trafic = sp.trafic;
    this.draft.vs = sp.vs;
    this.draft.state = sp.state ?? true;
    this.editorOpen = true;
  }

  public deletePoint(key: 'aq' | 'mq' | 'blinker' | 'do' | 'di', sp: ProjectTimeSwitchInterface): void {
    const id = this.selectedTimer?.id;
    if (!id || sp.id === undefined) { return; }
    this.modalService.openDialog('Schaltpunkt löschen?', 'Nein', 'Ja').subscribe(() => {
      const obs = key === 'aq' ? this.timerService.deleteAQTimer(id, sp.id!)
        : key === 'mq' ? this.timerService.deleteMQTimer(id, sp.id!)
          : key === 'blinker' ? this.timerService.deleteBlinkerTimer(id, sp.id!)
            : key === 'do' ? this.timerService.deleteDoTimer(id, sp.id!)
              : this.timerService.deleteDiTimer(id, sp.id!);
      obs.subscribe(() => {
        this.alertService.alert('Schaltpunkt gelöscht', AlertTypes.info);
        this.loadTimerSwitches();
      });
    });
  }

  /** Verschiebt einen Schaltpunkt im Vorrang-Stapel und schreibt die neue Reihenfolge als Priorität. */
  public movePoint(key: 'aq' | 'mq' | 'blinker' | 'do' | 'di', index: number, direction: 'up' | 'down'): void {
    const id = this.selectedTimer?.id;
    if (!id) { return; }
    const list = this.managedPoints(key);
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= list.length) { return; }
    [list[index], list[target]] = [list[target], list[index]];

    const calls = list.map((sp, i) => { sp.priority = i; return this.updateObservable(key, id, sp); });
    if (calls.length === 0) { return; }
    forkJoin(calls).subscribe(() => this.loadTimerSwitches());
  }

  private updateObservable(key: 'aq' | 'mq' | 'blinker' | 'do' | 'di', timerSwitchId: number, sp: ProjectTimeSwitchInterface): Observable<any> {
    switch (key) {
      case 'aq': return this.timerService.updateAQTimer(timerSwitchId, sp);
      case 'mq': return this.timerService.updateMQTimer(timerSwitchId, sp);
      case 'blinker': return this.timerService.updateBlinkerTimer(timerSwitchId, sp);
      case 'do': return this.timerService.updateDoTimer(timerSwitchId, sp.id!, sp);
      case 'di': return this.timerService.updateDiTimer(timerSwitchId, sp.id!, sp);
    }
  }
}
